import { useQuery } from '@tanstack/react-query'
import { Table } from 'antd'
import _ from 'lodash'
import moment from 'moment'
import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import ErrorModalWithTimer from '@Modal/ErrorModalWithTimer/index.jsx'
import SuccessModalWithTimer from '@Modal/SuccessModalWithTimer/index.jsx'
import ViewProductionWorkersModal from '@Modal/viewProductionWorkerModal.jsx'
import { columnProductionInSalesModal } from '@utils/columns.jsx'
import { fetchProductionInSalesModal } from '@utils/fetchData.js'


import './table.css'
import { switchBgColor, meaningOfBg } from '@utils/switchBgProductionsTables'
import { isAfter, isBefore, isSameDay } from '@utils/time'

const ProductionInViewSales = ({ docEntry }) => {
	const { t } = useTranslation()
	const { roleNames, getMe } = useSelector((state) => state.main)
	const successRef = useRef()
	const errorRef = useRef()
	const tableRef = useRef(null)

	const formattedDate = moment()

	const [currentPage, setCurrentPage] = useState(0)
	const [isModalVisible, setIsModalVisible] = useState(false)
	const [selectedRowData, setSelectedRowData] = useState(null)
	const [productionWorkersData, setProductionWorkersData] = useState([])
	const [hasMore, setHasMore] = useState(false)
	const [isFetching, setIsFetching] = useState(false)

	const [pageSize, setPageSize] = useState(20)
	const [isStarted, setIsStarted] = useState('')
	const [isReady, setIsReady] = useState('')
	const [isReadyForNextStage, setIsReadyForNextStage] = useState('')
	const [startDate, setStartDate] = useState('')
	const [endDate, setEndDate] = useState('')
	const [isBroken, setIsBroken] = useState('')

	const meaningBg = meaningOfBg(t)

	const {
		data: productionWorker = [],
		isLoading,
		refetch,
	} = useQuery({
		queryKey: [
			'productionSalesOrder',
			currentPage,
			pageSize,
			docEntry,
			isStarted,
			isReady,
			isReadyForNextStage,
			startDate,
			endDate,
			isBroken,
		],
		queryFn: fetchProductionInSalesModal,
		keepPreviousData: false,
		refetchOnWindowFocus: true,
		enabled: !isFetching || docEntry !== null,
	})

	useEffect(() => {
		if (productionWorker.length === 0) {
			setHasMore(false)
		} else {
			setProductionWorkersData((prev) => [...prev, ...productionWorker])
			setHasMore(productionWorker.length === pageSize)
		}
		setIsFetching(false)
	}, [productionWorker])

	const handleScroll = _.debounce(() => {
		if (tableRef.current) {
			const { scrollTop, scrollHeight, clientHeight } = tableRef.current

			if (
				scrollTop + clientHeight >= scrollHeight - 100 &&
				!isFetching &&
				hasMore
			) {
				setIsFetching(true)
				setCurrentPage((prev) => prev + 1)
			}
		}
	}, 200)

	useEffect(() => {
		const tableContainer =
			tableRef.current?.getElementsByClassName('ant-table-body')[0]
		if (tableContainer) {
			tableContainer.addEventListener('scroll', handleScroll)
			return () => {
				tableContainer.removeEventListener('scroll', handleScroll)
				handleScroll.cancel()
			}
		}
	}, [hasMore, isFetching])

	useEffect(() => {
		if (isFetching) {
			refetch()
		}
	}, [isFetching, refetch])

	const handleFilter = (meaning) => {
		if (meaning === 'Done') {
			setIsReady('Yes')
			setIsStarted('Yes')
			setIsReadyForNextStage('')
			setStartDate('')
			setEndDate(moment().endOf('year').format('YYYY-MM-DD'))
			setProductionWorkersData([])
			setCurrentPage(0)
		}
		else if (meaning === 'Broken') {
			setIsReady('')
			setIsStarted('')
			setIsReadyForNextStage('')
			setStartDate('')
			setEndDate(moment().endOf('year').format('YYYY-MM-DD'))
			setProductionWorkersData([])
			setCurrentPage(0)
			setIsBroken('Y')
		} else if (meaning === 'InProcess') {
			setIsReadyForNextStage('Yes')
			setIsReady('No')
			setIsStarted('Yes')
			setStartDate('')
			setEndDate(moment().endOf('year').format('YYYY-MM-DD'))
			setProductionWorkersData([])
			setCurrentPage(0)
						setIsBroken('')

		} else if (meaning === 'NotStarted') {
			setIsReadyForNextStage('Yes')
			setIsReady('No')
			setIsStarted('No')
			setStartDate(moment().format('YYYY-MM-DD'))
			setEndDate(moment().endOf('year').format('YYYY-MM-DD'))
			setCurrentPage(0)
			setProductionWorkersData([])
									setIsBroken('')

		} else if (meaning === 'Expired') {
			setIsReadyForNextStage('Yes')
			setIsReady('No')
			setIsStarted('No')
			setStartDate('')
			setEndDate(moment().format('YYYY-MM-DD'))
			setProductionWorkersData([])
			setCurrentPage(0)
									setIsBroken('')

		} else if (meaning === 'HasToDoToday') {
			setIsReadyForNextStage('Yes')
			setIsReady('No')
			setIsStarted('No')
			setStartDate(moment().format('YYYY-MM-DD'))
			setEndDate(moment().format('YYYY-MM-DD'))
			setProductionWorkersData([])
			setCurrentPage(0)
									setIsBroken('')

		} else if (meaning === 'NotReadyForNextStage') {
			setIsReadyForNextStage('No')
			setIsReady('')
			setIsStarted('')
			setStartDate('')
			setEndDate(moment().endOf('year').format('YYYY-MM-DD'))
			setProductionWorkersData([])
			setCurrentPage(0)
									setIsBroken('')

		} else if (meaning === 'ClearAll') {
			setIsReadyForNextStage('')
			setIsReady('')
			setIsStarted('')
			setStartDate('')
			setEndDate(moment().endOf('year').format('YYYY-MM-DD'))
			setCurrentPage(0)
			setProductionWorkersData([])
									setIsBroken('')

		}
	}

	const columns = columnProductionInSalesModal(t)

	return (
		<>
			<div className="px-4">
				<div className="mt-10 mb-4 flex flex-col justify-between sm:flex-row">
					<div className="flex gap-5 flex-wrap">
						{meaningBg.map((item) => (
							<div
								key={item.id}
								className="flex gap-1 items-center"
								onClick={() => handleFilter(item.key)}
							>
								<div
									className={`rounded-full ${item.className} w-[20px] h-[20px]`}
								></div>
								<span>{item.meaning}</span>
							</div>
						))}
					</div>
				</div>

				<div
					ref={tableRef}
					className="max-h-[500px] mt-5 overflow-auto border rounded-lg shadow-md"
				>
					<Table
						columns={columns}
						dataSource={productionWorkersData}
						pagination={false}
						className="w-full ant-table-override"
						rowKey={(record) => record.key}
						bordered
						scroll={{
							y: 500,
							x: 900,
							scrollToFirstRowOnChange: true,
						}}
						sticky={{
							offsetHeader: 0,
							offsetScroll: 0,
							getContainer: () => window,
						}}
						loading={isLoading || isFetching}
						rowClassName={switchBgColor}
					/>
				</div>
			</div>

			<ViewProductionWorkersModal
				visible={isModalVisible}
				data={selectedRowData}
				onClose={() => setIsModalVisible(false)}
			/>

			<SuccessModalWithTimer
				getRef={(r) => {
					successRef.current = r
				}}
			/>

			<ErrorModalWithTimer
				getRef={(r) => {
					errorRef.current = r
				}}
			/>
		</>
	)
}

export default ProductionInViewSales
